import { authConfig } from '../config/auth';
import {
  Poll,
  CreatePollRequest,
  VoteRequest,
  ApiResponse,
  PollFeedResponse,
  UserProfile,
  CategorizePollRequest,
  CategorizePollResponse,
} from '../types';

class ApiService {
  private baseUrl: string;
  private idToken: string | null = null;

  constructor() {
    this.baseUrl = authConfig.apiUrl;
    this.idToken = this.getIdTokenFromStorage();
  }

  setIdToken(token: string) {
    this.idToken = token;
    localStorage.setItem('idToken', token);
  }

  getIdTokenFromStorage(): string | null {
    return localStorage.getItem('idToken');
  }

  clearIdToken() {
    this.idToken = null;
    localStorage.removeItem('idToken');
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // 1. If response is not OK (e.g. 4xx, 5xx), handle gracefully without crashing on non-JSON content
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          console.warn('Failed to parse error response body:', e);
        }
        throw new Error(errorMessage);
      }

      // 2. If response is OK, ensure it contains valid JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      // Fallback for non-JSON or empty successful responses
      const responseText = await response.text();
      if (!responseText) {
        return { success: true } as unknown as ApiResponse<T>;
      }

      throw new Error('Expected JSON response from server');
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async createPoll(request: CreatePollRequest): Promise<Poll> {
    const response = await this.makeRequest<Poll>('/polls', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create poll');
    }

    return response.data;
  }

  async listPolls(limit: number = 20, lastKey?: string, searchQuery?: string, category?: string, includeClosed?: boolean): Promise<PollFeedResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (lastKey) {
      params.append('lastKey', lastKey);
    }
    if (searchQuery?.trim()) {
      params.append('search', searchQuery.trim());
    }
    if (category) {
      params.append('category', category);
    }
    if (includeClosed) {
      params.append('includeClosed', 'true');
    }

    const response = await this.makeRequest<PollFeedResponse>(
      `/polls?${params.toString()}`,
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch polls');
    }

    return response.data;
  }

  async voteOnPoll(pollId: string, optionId: string): Promise<Poll> {
    const request: VoteRequest = { optionId };

    const response = await this.makeRequest<Poll>(`/polls/${pollId}/vote`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to vote on poll');
    }

    return response.data;
  }

  async unvoteOnPoll(pollId: string): Promise<Poll> {
    const response = await this.makeRequest<Poll>(`/polls/${pollId}/vote`, {
      method: 'DELETE',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to remove vote');
    }

    return response.data;
  }

  async getUserProfile(userId: string): Promise<UserProfile> {
    const response = await this.makeRequest<UserProfile>(`/users/${userId}`, {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch user profile');
    }

    return response.data;
  }

  async getUserActivity(userId: string): Promise<{
    voted: Poll[];
    skipped: Poll[];
    created: Poll[];
  }> {
    const response = await this.makeRequest<{
      voted: Poll[];
      skipped: Poll[];
      created: Poll[];
    }>(`/users/${userId}/activity`, {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch user activity');
    }

    return response.data;
  }

  async getPoll(pollId: string): Promise<Poll> {
    const response = await this.makeRequest<Poll>(`/polls/${pollId}`, {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch poll');
    }

    return response.data;
  }

  async getNextPoll(): Promise<Poll | null> {
    try {
      const response = await this.makeRequest<Poll>(`/polls/next`, {
        method: 'GET',
      });

      if (!response.success || !response.data) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      // If the error is "no more polls" or "No polls available", return null instead of throwing
      if (error.message?.includes('No more unseen polls') ||
          error.message?.includes('No polls available')) {
        return null;
      }
      throw error;
    }
  }

  async deletePoll(pollId: string): Promise<void> {
    const response = await this.makeRequest<void>(`/polls/${pollId}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete poll');
    }
  }

  async closePoll(pollId: string): Promise<Poll> {
    const response = await this.makeRequest<Poll>(`/polls/${pollId}/close`, {
      method: 'POST',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to close poll');
    }

    return response.data;
  }

  async skipPoll(pollId: string): Promise<void> {
    const response = await this.makeRequest<void>(`/polls/${pollId}/skip`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to skip poll');
    }
  }

  async categorizePoll(request: CategorizePollRequest): Promise<CategorizePollResponse> {
    const response = await this.makeRequest<CategorizePollResponse>('/categorize', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to categorize poll');
    }

    return response.data;
  }

  async syncProfileImage(): Promise<{ updated: number }> {
    const response = await this.makeRequest<{ updated: number }>('/users/me/sync-image', {
      method: 'POST',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to sync profile image');
    }

    return response.data;
  }

  async reportPoll(pollId: string, reason: string): Promise<void> {
    const response = await this.makeRequest<void>(`/polls/${pollId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to report poll');
    }
  }

  async listFlaggedPolls(): Promise<Poll[]> {
    const response = await this.makeRequest<Poll[]>('/admin/polls', {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch flagged polls');
    }

    return response.data;
  }

  async approveFlaggedPoll(pollId: string): Promise<void> {
    const response = await this.makeRequest<void>(`/admin/polls/${pollId}/approve`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to approve poll');
    }
  }
}

export const apiService = new ApiService();
